import { useCallback, useEffect, useRef, useState } from 'react'
import { createOrder, verifyOrder, type CheckoutOrder } from '@/api/billing'
import { refreshCredits } from '@/store/creditsStore'

/**
 * Launch a gateway checkout for any purchase and settle it.
 *
 * Provider-agnostic seam: the caller asks for a purpose + target and gets back
 * a completed purchase. Only the script loading and the `window.Razorpay`
 * handshake below know which gateway is in play, so a second provider is a
 * branch here — not a change at every call site.
 *
 * The client verify call is a courtesy, not the source of truth: the webhook
 * finalizes the order server-side and both paths converge on the same
 * idempotent `finalize_paid_order`, so credits are granted exactly once even if
 * the browser is closed the instant after payment.
 */

interface GatewayInstance {
  open: () => void
  close: () => void
  on: (event: string, cb: (r: unknown) => void) => void
}

/**
 * The gateway's global, read through a local cast rather than a `declare
 * global`: PaymentGatewayPage already declares `window.Razorpay` with its own
 * option shape, and a second global declaration would collide with it.
 */
function gateway(): (new (options: Record<string, unknown>) => GatewayInstance) | undefined {
  return (window as unknown as {
    Razorpay?: new (options: Record<string, unknown>) => GatewayInstance
  }).Razorpay
}

const RAZORPAY_SRC = 'https://checkout.razorpay.com/v1/checkout.js'

export interface CheckoutRequest {
  purpose: string
  target_ref?: string
  billing_period?: string
  coupon_code?: string
  project_ref?: string
  /** Shown in the gateway modal. */
  description?: string
  /** Create the order through a purpose-specific endpoint instead of the
   *  generic /checkout/order (e.g. subscription renewal, project top-up). */
  precreate?: () => Promise<CheckoutOrder>
}

export interface CheckoutResult {
  ok: boolean
  cancelled?: boolean
  error?: string
  order_id?: string
  invoice_number?: string | null
  balance?: number
}

function apiError(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { detail?: string; message?: string } }; message?: string }
  const detail = e?.response?.data?.detail
  if (typeof detail === 'string') return detail
  return e?.response?.data?.message || e?.message || fallback
}

/** Load the gateway script once per page. Resolves false if it can't load. */
function loadGatewayScript(): Promise<boolean> {
  if (typeof window === 'undefined') return Promise.resolve(false)
  if (gateway()) return Promise.resolve(true)
  return new Promise((resolve) => {
    let script = document.querySelector<HTMLScriptElement>(`script[src="${RAZORPAY_SRC}"]`)
    if (!script) {
      script = document.createElement('script')
      script.src = RAZORPAY_SRC
      script.async = true
      document.body.appendChild(script)
    }
    script.addEventListener('load', () => resolve(true), { once: true })
    script.addEventListener('error', () => resolve(false), { once: true })
    // Already-loaded tag that fired before we attached the listener.
    if (gateway()) resolve(true)
  })
}

export function useRazorpayCheckout() {
  const [busy, setBusy] = useState(false)
  const activeRef = useRef(false)

  useEffect(() => () => { activeRef.current = false }, [])

  const checkout = useCallback(async (req: CheckoutRequest): Promise<CheckoutResult> => {
    if (activeRef.current) return { ok: false, error: 'A checkout is already open' }
    activeRef.current = true
    setBusy(true)
    try {
      const scriptOk = await loadGatewayScript()
      if (!scriptOk) {
        return { ok: false, error: 'Could not load the payment gateway. Check your connection and try again.' }
      }

      let created: CheckoutOrder
      try {
        created = req.precreate
          ? await req.precreate()
          : await createOrder({
              purpose: req.purpose,
              target_ref: req.target_ref,
              billing_period: req.billing_period,
              coupon_code: req.coupon_code,
              project_ref: req.project_ref,
            })
      } catch (err) {
        return { ok: false, error: apiError(err, 'Could not start the payment.') }
      }

      const keyId = created.provider_config?.key_id
      if (!keyId) {
        return { ok: false, error: 'The payment gateway is not configured. Contact your administrator.' }
      }

      const settled = await new Promise<CheckoutResult>((resolve) => {
        const base: Record<string, unknown> = {
          key: keyId,
          name: 'Twasta.ai',
          description: req.description || 'Twasta.ai purchase',
          theme: { color: '#1976d2' },
          modal: {
            // Dismissal is a cancellation, not a failure — the order stays
            // `pending` and payment reconciliation closes it out later.
            ondismiss: () => resolve({ ok: false, cancelled: true, order_id: created.order.id }),
          },
          handler: async (response: Record<string, string>) => {
            try {
              const result = await verifyOrder({
                order_id: created.order.id,
                provider_payment_id: response.razorpay_payment_id,
                provider_order_id: response.razorpay_order_id,
                provider_subscription_id: response.razorpay_subscription_id,
                signature: response.razorpay_signature,
              })
              refreshCredits()
              resolve({
                ok: true,
                order_id: created.order.id,
                invoice_number: result?.invoice_number ?? null,
                balance: result?.balance,
              })
            } catch (err) {
              // The money may well have been taken — say so honestly rather
              // than claiming failure. The webhook settles it either way.
              resolve({
                ok: false,
                order_id: created.order.id,
                error: apiError(
                  err,
                  'Payment went through but confirmation failed. Your credits will appear shortly.',
                ),
              })
            }
          },
        }

        if (created.checkout.mode === 'subscription') {
          base.subscription_id = created.checkout.subscription_id
        } else {
          base.order_id = created.checkout.order_id
          base.amount = created.order.total_minor
          base.currency = created.order.currency
        }

        const Gateway = gateway()
        if (!Gateway) {
          resolve({ ok: false, error: 'The payment gateway did not load. Try again.' })
          return
        }
        const rzp = new Gateway(base)
        rzp.on('payment.failed', (r: unknown) => {
          const failure = r as { error?: { description?: string } }
          resolve({
            ok: false,
            order_id: created.order.id,
            error: failure?.error?.description || 'The payment was declined.',
          })
        })
        rzp.open()
      })

      return settled
    } finally {
      activeRef.current = false
      setBusy(false)
    }
  }, [])

  return { checkout, busy }
}
